import React, { useState, useEffect, Suspense, useRef, lazy } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { MantineProvider, Loader } from "@mantine/core";
import { ToastContainer, toast, Flip, Id } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNetwork } from "@mantine/hooks";
import { auth } from "./firebase/FirebaseCredential";
import PrivateRoute from "./PrivateRoute";
import ProtectedRoute from "./ProtectedRoute";
import SubscriptionRoute from "./SubscriptionRoute";
import { useAppSelector } from "./hooks/hooks";
import App from "./App";
import { TopNavLoader } from "./Components/UtilityComponents/PageLoader";
import { AnimatePresence } from "framer-motion";
import AuthenticatedBlankLayout from "./Components/LayoutComponent/AuthenticatedBlankLayout";
import ForgotPassword from "./Components/ForgotPageComponent/ForgotPage";
import { ROUTES } from "./routes/routesPaths";
import Routinepage from "./Components/UIComponent/Routinepage";
import ExerciseDetailPage from "./Components/UIComponent/ExerciseDetailPage";
import BranchDetailspage from "./Components/UIComponent/BranchDetailspage";
import UIViewPage from "./Components/pages/UIViewPage";
import FormBuilder from "./Components/FormBuilder/FormBuilder";
import FormsList from "./Components/FormBuilder/FormsList";
import LandingPagesList from "./Components/LandingPageModule/LandingPagesList";
import LandingPageBuilder from "./Components/LandingPageModule/LandingPageBuilder";
import LandingPageViewProxy from "./Components/LandingPageModule/LandingPageViewProxy";
import CommunicationConfigPage from "./Components/CommunicationConfigComponent/CommunicationConfigPage";
import GSTSlabConfigPage from "./Components/CommunicationConfigComponent/Component/GSTSlabConfigPage";
import GracePeriodConfigPage from "./Components/CommunicationConfigComponent/Component/GracePeriodConfigPage";
import UploadRoutine from "./Components/RoutinePlanComponent/UploadRoutine";
import PayrollMainPage from "./Components/PayrollComponent/PayrollMainPage";
import PayrollEmployeesPage from "./Components/PayrollComponent/PayrollEmployeesPage";
import MyPayrollReport from "./Components/PayrollComponent/MyPayrollReport";
import ApplyLeavePage from "./Components/ApplyLeave/LeaveSummaryPage";
import LeaveSummaryPage from "./Components/ApplyLeave/LeaveSummaryPage";
import EnquiryEditPage from "./Components/EnquiryComponent/EnquiryEditPage";
import SpaBillingPage from "./Components/modules/Pos/SpaBillingPage";
import ProductImportPage from "./ProductComponent/ProductImportPage";
import ProductSalesReport from "./Components/ReportComponent/ProductSalesReport";
import PackageSalesReport from "./Components/ReportComponent/PackageSalesReport/PackageSalesReport";
import ClassesGymPage from "./Components/modules/classes/gym/pages/ClassesGymPage/ClassesGymPage";
import { TermsAndConditions } from "./Components/DashboardComponent/TermsAndConditions";
import AppointmentBookingPage from "./Components/pages/publicpages/AppointmentBookingPage";
import NotificationTestPage from "./Components/pages/Testing/NotificationTestPage";
import Trainers from "./Components/TrainersComponent/Trainers";
import MemberRoutineGenerator from "./Components/RoutinePlanComponent/RoutineGenerator/MemberRoutineGenerator";
import RoutineTemplate from "./Components/RoutinePlanComponent/RoutineTemplate";
import MemberFreeze from "./Components/Customization/MemberFreeze";
import EnquirySettings from "./Components/Customization/EnquirySettings";
import RecuringExpenses from "./Components/ExpenseComponent/RecuringExpenses";
import RecurringExpenseTable from "./Components/ExpenseComponent/RecurringExpenseTable";
import ChallengeViewPage from "./Components/Challenges/ChallengeViewPage";
import ExternalMembersTable from "./Components/MemberComponent/ExternalMembersTable";
import ReferralSettings from "./Components/CompanySettingComponent/subComponent/ReferralComponent/ReferralSettings";
const TrainerDetailsPage = lazy(
  () => import("./Components/TrainersComponent/TrainerDetailsPage"),
);
const ClassesImportPage = lazy(
  () => import("./Components/modules/classes/gym/pages/ClassesImportPage"),
);
const ClassPage = lazy(
  () => import("./Components/ClassesComponent/ClassComponent/ClassPage"),
);
const PublicClassBookingPage = lazy(
  () => import("./Components/pages/publicpages/PublicClassBookingPage"),
);
const ClassInstancePage = lazy(
  () =>
    import("./Components/ClassesComponent/ClassComponent/ClassInstancePage"),
);
const ClassBookingPage = lazy(
  () => import("./Components/ClassesComponent/ClassComponent/ClassBookingPage"),
);

const CreateClasses = lazy(
  () => import("./Components/ClassesComponent/ClassComponent/createclasses"),
);
const ClassDetailsPage = lazy(
  () => import("./Components/ClassesComponent/ClassComponent/ClassDetailsPage"),
);
const ClassInstanceDetailsPage = lazy(
  () =>
    import("./Components/ClassesComponent/ClassComponent/ClassInstanceDetailsPage"),
);
const ClassReportPage = lazy(
  () => import("./Components/ClassesComponent/ClassComponent/ClassReportPage"),
);
const RoomPage = lazy(
  () => import("./Components/ClassesComponent/RoomComponent/RoomPage"),
);
const ScheduleDetailsPage = lazy(
  () =>
    import("./Components/ClassesComponent/ClassComponent/ScheduleDetailsPage"),
);

const AddProductPage = lazy(() => import("./ProductComponent/AddProduct"));
const AllProductsPage = lazy(() => import("./ProductComponent/Product"));
const EditProductPage = lazy(() => import("./ProductComponent/EditProduct"));
const ProductDetailsPage = lazy(
  () => import("./ProductComponent/ProductViewPage"),
);
const InventoryPage = lazy(() => import("./InventoryComponent/Inventory"));
const InventoryViewPage = lazy(
  () => import("./InventoryComponent/InventoryViewPage"),
);
const AdjustStockPage = lazy(
  () => import("./InventoryComponent/AdjustStockPage"),
);
const RewardsPage = lazy(() => import("./Components/Rewards/RewardsPage"));
const PurchaseOrderPage = lazy(
  () => import("./PurchaseOrderComponent/PurchaseOrderPage"),
);
const CreatePurchaseOrderPage = lazy(
  () => import("./PurchaseOrderComponent/CreatePurchaseOrderPage"),
);
const PurchaseOrderDetailsPage = lazy(
  () => import("./PurchaseOrderComponent/PurchaseOrderDetailsPage"),
);

const BillsPage = lazy(() => import("./BillsComponent/BillsPage"));
const BillDetailsPage = lazy(() => import("./BillsComponent/BillDetailsPage"));

// import AddProductPage from "./Components/modules/appointment/spa/pages/AddProductPage";
const LeaveApprovalPage = lazy(
  () => import("./Components/ApplyLeave/LeaveApprovalPage"),
);
// import Demo from "./Components/MemberComponent/UserProfileComponent/Demo";
// import RoutineExerciseViewPage from "./Components/RoutinePlanComponent/RoutineExerciseViewPage";
const CreateChallenge = lazy(
  () => import("./Components/Challenges/CreateChallenge"),
);
const ChallengesTablePage = lazy(
  () => import("./Components/Challenges/ChallengesTablePage"),
);
const WhatsApp = lazy(() => import("./Components/whatsapp/whatsapp"));
const WhatsAppConfigPage = lazy(
  () => import("./Components/whatsapp/WhatsAppTemplateSettings"),
);
const WhatsAppTemplateMapping = lazy(
  () => import("./Components/whatsapp/WhatsAppTemplateMapping"),
);
const WhatsAppRegistration = lazy(
  () => import("./Components/whatsapp/WhatsAppRegistration"),
);
const SMS = lazy(() => import("./Components/sms/sms"));
const SMSConfigPage = lazy(
  () => import("./Components/sms/SMSTemplateSettings"),
);
const SMSRegistration = lazy(() => import("./Components/sms/SMSRegistration"));
const SMSNotification = lazy(() => import("./Components/sms/SMSNotification"));
const SMSTemplateMapping = lazy(
  () => import("./Components/sms/SMSTemplateMapping"),
);
const Email = lazy(() => import("./Components/email/email"));
const EmailSMTPConfig = lazy(
  () => import("./Components/email/EmailSMTPConfig"),
);

const StaffSalaryPage = lazy(
  () => import("./Components/userComponent/StaffSalaryPage"),
);
const ExcersiceCreatepage = lazy(
  () => import("./Components/RoutinePlanComponent/ExcersiceCreatepage"),
);
const CertificateUploadPage = lazy(
  () => import("./Components/userComponent/CertificateUploadPage"),
);
const BiometricDevicePage = lazy(
  () => import("./Components/BiometricDevice/BiometricDevicePage"),
);
const AddBiometricDevicePage = lazy(
  () => import("./Components/BiometricDevice/AddBiometricDevicePage"),
);
const EditBiometricDevicePage = lazy(
  () => import("./Components/BiometricDevice/EditBiometricDevicePage"),
);
const ForgotPage = lazy(
  () => import("./Components/ForgotPageComponent/ForgotPage"),
);
const ResetPassword = lazy(
  () => import("./Components/ForgotPageComponent/ResetPassword"),
);
const Subscription = lazy(
  () => import("./Components/SubscriptionComponent/Subscription"),
);
const EnquiryViewPage = lazy(
  () => import("./Components/EnquiryComponent/EnquiryViewPage"),
);
const PackageViewPage = lazy(
  () => import("./Components/PackageComponent/PackageViewPage"),
);

const UPIEditPage = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/PaymentComponent/UPIEditPage"),
);

// import ClassesGymPage from "./Components/modules/classes/gym/pages/ClassesGymPage/ClassesGymPage";
// import ClassesCreateGymPage from "./Components/modules/classes/gym/pages/ClassesCreateGymPage";
// import TutorManagementGymPage from "./Components/modules/classes/gym/pages/TutorManagementGymPage";
// import SPABookingPage from "./Components/modules/appointment/spa/pages/SPABookingPage";
// import TurfBookingPage from "./Components/modules/turfbooking/turf/pages/TurfBookingPage";
// import TurfSetupPage from "./Components/modules/turfbooking/turf/pages/TurfSetupPage";
// import BookingSuccessPage from "./Components/pages/publicpages/BookingSuccessPage";

// import AddStaffPage from "./Components/modules/appointment/spa/pages/AddStaffPage";
// import BookAppointmentPage from "./Components/modules/appointment/spa/pages/BookAppointmentPage";
// import AddServicePage from "./Components/modules/appointment/spa/pages/AddServicePage";
// import AddProductPage from "./Components/modules/appointment/spa/pages/AddProductPage";
// import SpaBillingPage from "./Components/modules/Pos/SpaBillingPage";
// import Memberships from "./Components/modules/appointment/spa/pages/Memberships";
// import MembershipsCreatePage from "./Components/modules/appointment/spa/pages/MembershipsCreatePage";
// import SpaConfigPage from "./Components/modules/appointment/spa/pages/SpaConfigPage";
const MemberSubscriptionPage = lazy(() => import("./Page/NewSubscriptionPage"));
//const DietPlanDetailsPage = lazy(() => import("./Components/DietPlan/DietPlanDetailsPage"));
const IntegrationsPage = lazy(() => import("./Page/IntegrationsPage"));
const PaymentReceivedViewPage = lazy(
  () => import("./Components/PaymentReceived/PaymentReceivedViewPage"),
);
const PaymentReceivedEditPage = lazy(
  () => import("./Components/PaymentReceived/PaymentReceivedEditPage"),
);
const RecurringPaymentPage = lazy(
  () => import("./Components/PaymentReceived/RecurringPaymentPage"),
);
const ExpenseViewPage = lazy(
  () => import("./Components/ExpenseComponent/ExpenseViewPage"),
);
const RecurringExpenseViewPage = lazy(
  () => import("./Components/ExpenseComponent/RecurringExpenseViewPage"),
);
const ExpenseEditPage = lazy(
  () => import("./Components/ExpenseComponent/ExpenseEditPage"),
);
const MemberQuickAddPage = lazy(
  () => import("./Components/MemberComponent/MemberQuickAddPage"),
);

// Lazy load components
const MemberProfile = lazy(
  () =>
    import("./Components/MemberComponent/UserProfileComponent/MemberProfile"),
);
const HomePage = lazy(() => import("./Components/HomeComponent/HomePage"));

const ProfilePage = lazy(
  () => import("./Components/ProfileComponent/ProfilePage"),
);
const BranchPage = lazy(
  () => import("./Components/BranchesComponent/BranchPage"),
);
const BranchDetailsPage = lazy(
  () =>
    import("./Components/BranchesComponent/BranchDetailsPageComponent/BranchDetailsPage"),
);
const MemberDetailsPage = lazy(
  () =>
    import("./Components/MemberComponent/MemberDetailsPageComponent/MemberDetailsPage"),
);
const Package = lazy(() => import("./Components/PackageComponent/Package"));
// const PackageImportPage = lazy(() => import("./Components/PackageComponent/PackageImportPage"));
const UserPage = lazy(() => import("./Components/userComponent/userPage"));
const AdminUserDetailPage = lazy(
  () => import("./Components/AdminPageComponent/AdminUserDetailPage"),
);
const ExpensePage = lazy(
  () => import("./Components/ExpenseComponent/ExpensePage"),
);
const MemberEditPage = lazy(
  () => import("./Components/MemberComponent/MemberEditPage"),
);
const EnquiryPage = lazy(
  () => import("./Components/EnquiryComponent/EnquiryPage"),
);
const CompanyProfilePage = lazy(
  () => import("./Components/CompanyProfileComponent/CompanyProfile"),
);
const AddTransactionPage = lazy(
  () => import("./Components/ExpenseComponent/AddTransactionPage"),
);
const EmployeeAttendance = lazy(
  () =>
    import("./Components/ReportComponent/AttendanceReport/EmployeeAttendance"),
);
const ExpenseReport = lazy(
  () => import("./Components/ReportComponent/ExpenseReport/ExpenseReport"),
);

const DashboardPage = lazy(
  () => import("./Components/DashboardComponent/DashboardPage"),
);
const TermsPage = lazy(() => import("./Components/pages/TermsPage"));

const MemberHistory = lazy(
  () =>
    import("./Components/MemberComponent/MemberHistoryComponent/MemberHistory"),
);
const RoutinePlanMain = lazy(
  () => import("./Components/RoutinePlanComponent/RoutinePlanMain"),
);
const RoutinePlanCreate = lazy(
  () =>
    import("./Components/RoutinePlanComponent/RoutinePlanCreate/RoutinePlanCreate"),
);
const RoutinePlanSinglePage = lazy(
  () =>
    import("./Components/RoutinePlanComponent/RoutinePlanSinglePage/RoutinePlanSinglePage"),
);
const RoutinePlanUpdate = lazy(
  () =>
    import("./Components/RoutinePlanComponent/RoutinePlanUpdate/RoutinePlanUpdate"),
);
const Chat = lazy(() => import("./Components/ChatComponent/Chat"));
const PayrollReportPage = lazy(
  () => import("./Components/ReportComponent/PayrollReport/PayrollReportPage"),
);
const EnquiryFormManagement = lazy(
  () =>
    import("./Components/EnquiryFormManagementComponent/EnquiryFormManagement"),
);
const EnquiryAddPage = lazy(
  () => import("./Components/EnquiryComponent/EnquiryAddPage"),
);
const EquipmentPage = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/EquipmentComponent/EquipmentPage"),
);
const EquipmentImportPage = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/EquipmentComponent/EquipmentImportPage"),
);
const AddEquipmentPage = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/EquipmentComponent/AddEquipmentPage"),
);
const MemberAddPage = lazy(
  () => import("./Components/MemberComponent/MemberAddPage"),
);
const AddBranchPage = lazy(
  () => import("./Components/BranchesComponent/AddBranchPage"),
);
const CompanyRegistration = lazy(
  () => import("./Components/CompanyRegistrationPage/CompanyRegistration"),
);
const SettingOverviewPage = lazy(
  () => import("./Components/SettingOverviewPage/SettingOverviewPage"),
);
const AddNewUser = lazy(
  () => import("./Components/userComponent/AddNewUser/AddNewUser"),
);
const CompanySettings = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/CompanySettings"),
);
const BusinessHoursPage = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/BusinessHoursComponent/BusinessHoursPage"),
);
const AttendanceSettingsView = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/AttendanceComponent/AttendanceSettingsView"),
);
const PayrollPage = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/PayrollComponent/PayrollPage"),
);
const HolidayPage = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/HolidayComponent/HolidayPage"),
);
const Banking = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/BankingComponent/Banking"),
);
const PaymentPage = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/PaymentComponent/PaymentPage"),
);
const MemberAttendanceSetting = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/AttendanceComponent/MemberAttendance"),
);
const PackageCreatePage = lazy(
  () => import("./Components/PackageComponent/PackageCreatePage"),
);
const AddBankingPage = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/BankingComponent/AddBankingPage"),
);
const PrefixPage = lazy(() => import("./Components/Customization/PrefixPage"));
const EmailTemplateManage = lazy(
  () => import("./Components/Customization/EmailTemplateManage"),
);
const EmailTemplateCreatePage = lazy(
  () => import("./Components/Customization/EmailTemplateCreatePage"),
);
const PaymentReceivedPage = lazy(
  () => import("./Components/PaymentReceived/PaymentReceivedPage"),
);
const AddPaymentReceivedPage = lazy(
  () => import("./Components/PaymentReceived/AddPaymentReceivedPage"),
);
const EquipmentUpdate = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/EquipmentComponent/EquipmentUpdate"),
);
const AddPaymentMethodPage = lazy(
  () =>
    import("./Components/CompanySettingComponent/subComponent/PaymentComponent/AddPaymentMethodPage"),
);

const InvoicePage = lazy(() => import("./Components/InvoicePage/InvoicePage"));
const AddInvoicePage = lazy(
  () => import("./Components/InvoicePage/AddInvoicePage"),
);
const InvoiceDetailPage = lazy(
  () => import("./Components/InvoicePage/InvoiceDetailPage"),
);
const MemberImportPage = lazy(
  () => import("./Components/ImportComponent/MemberImportpage"),
);
const SubscriptionPage = lazy(() => import("./Page/SubscriptionPage"));
const StaffLocationAccessControl = lazy(
  () => import("./Components/UserAccessControl/StaffLocationAccessControl"),
);
const GeoRestriction = lazy(
  () => import("./Components/UserAccessControl/GeoRestriction"),
);
const GeoRestrictionCreate = lazy(
  () => import("./Components/UserAccessControl/GeoRestrictionCreate"),
);
const IPRestrictionCreate = lazy(
  () => import("./Components/UserAccessControl/IPRestrictionCreate"),
);
const IPRestrictionEdit = lazy(
  () => import("./Components/UserAccessControl/IPRestrictionEdit"),
);
const GeoRestrictionEdit = lazy(
  () => import("./Components/UserAccessControl/GeoRestrictionEdit"),
);
const SubscriptionSuccess = lazy(
  () => import("./Page/SubscriptionSuccessPage"),
);
const MemberSubscriptionSuccess = lazy(
  () => import("./Page/MemberSubscriptionSuccessPage"),
);
const PaymentFailed = lazy(() => import("./Page/PaymentFailed"));
const BenchPaymentPage = lazy(() => import("./Page/BenchPaymentPage"));
const NewSubscriptionPage = lazy(
  () => import("./Components/DashboardComponent/NewSubscriptionPage"),
);
const CompanyRegistrationNotFound = lazy(
  () =>
    import("./Components/CompanyRegistrationPage/CompanyRegistrationNotFound"),
);
const MapBranchesToCompany = lazy(
  () => import("./Components/DashboardComponent/MapBranchesToCompany"),
);
const CompanyClaimed = lazy(
  () => import("./Components/CompanyRegistrationPage/CompanyClaimed"),
);
const CompanyRegistrationSearch = lazy(
  () =>
    import("./Components/CompanyRegistrationPage/CompanyRegistrationSearch"),
);
const ChooseCompanyType = lazy(
  () => import("./Components/CompanyRegistrationPage/ChooseCompanyType"),
);
const CompanyRegisterWizard = lazy(
  () => import("./Components/CompanyRegistrationPage/CompanyRegisterWizard"),
);
const BranchUpdate = lazy(
  () =>
    import("./Components/BranchesComponent/BranchUpdateComponent/BranchUpdate"),
);
const BranchImageupdate = lazy(
  () => import("./Components/BranchesComponent/BranchImageupdate"),
);
// import { RootState } from "./store/store";
const CompanyRegistrationModal = lazy(
  () => import("./Components/DashboardComponent/CompanyRegistrationModal"),
);
const ApiHealthChecker = lazy(
  () => import("./Components/UIComponent/Diagnostics/ApiHealthChecker"),
);
// Lazy-loaded components
const LoginPage = lazy(() => import("./Components/LoginComponent/LoginPage"));
const NotFoundPage = lazy(
  () => import("./Components/NotFoundPageComponent/NotFoundPage"),
);
const UserGuide = lazy(
  () => import("./Components/UserGuideComponent/UserGuide"),
);
const NewAccountCreate = lazy(
  () => import("./Components/AccountCreateComponent/NewAccountCreate"),
);
const InvalidRegistration = lazy(
  () =>
    import("./Components/pages/InvalidRegistrationComponent/InvalidRegistration"),
);
const PayinviteExpired = lazy(
  () => import("./Components/paymentUtils/PayinviteExpired"),
);
const StaffImportPage = lazy(
  () => import("./Components/ImportComponent/StaffImportPage"),
);

// Loan Components
const LoanTypesPage = lazy(() => import("./Components/Loan/LoanTypesPage"));
const LoanList = lazy(() => import("./Components/Loan/LoanList"));
const LoanDetails = lazy(() => import("./Components/Loan/LoanDetails"));
const GrantLoanPage = lazy(() => import("./Components/Loan/GrantLoanPage"));
const Action = lazy(() => import("./Action/Action"));
type Actions = {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
};

// type RouteProp = {
//   path: string;
//   name: string;
//   page_access: boolean;
//   actions: Actions;
// };
function AuthenticatedApp() {
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const toastId = useRef<Id | null>(null);
  const { online } = useNetwork();

  useEffect(() => {
    if (!online && !toastId.current) {
      toastId.current = toast.error("You are now offline", {
        transition: Flip,
        autoClose: false,
        position: "top-center",
      });
    } else if (online && toastId.current) {
      toast.dismiss(toastId.current);
      toastId.current = null;
    }
  }, [online]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsLoggedIn(!!user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);
  const roleAccess = useAppSelector((state) => state.roleAccess.value);
  // console.log('%roleAccess', 'color: blue;', roleAccess);
  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <Loader size="xl" variant="bars" />
      </div>
    );
  }
  function getActionPermissionForRoute(route: string): Actions {
    const matchedRoute = roleAccess.find(
      (r: { path: string }) => r.path === route,
    );
    return matchedRoute
      ? matchedRoute.actions
      : {
          create: false,
          read: false,
          update: false,
          delete: false,
        };
  }

  return (
    <AnimatePresence mode="wait">
      <Suspense
        fallback={
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <Loader size="xl" variant="bars" />
          </div>
        }
      >
        <BrowserRouter>
          <TopNavLoader />
          <Routes>
            {/* Public Routes */}
            <Route
              path={ROUTES.LOGIN}
              element={<LoginPage setIsLoggedIn={setIsLoggedIn} />}
            />
            <Route
              path={ROUTES.ACCOUNT_REGISTER}
              element={<NewAccountCreate />}
            />
            <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPage />} />
            <Route path={ROUTES.RESET_PASSWORD} element={<Action />} />
            <Route
              path={ROUTES.INVALID_PAGE}
              element={<InvalidRegistration />}
            />
            <Route
              path={ROUTES.PAY_LINK_EXPIRED}
              element={<PayinviteExpired />}
            />
            <Route path={ROUTES.ACTION} element={<Action />} />
            <Route path={ROUTES.TERMS} element={<TermsPage />} />
            <Route
              path={ROUTES.PUBLIC_CLASS_BOOKING}
              element={<PublicClassBookingPage />}
            />
            <Route
              path="/LandingPage/pageHtml"
              element={<LandingPageViewProxy />}
            />
            {/* Private Routes */}
            <Route
              element={<PrivateRoute element={<AuthenticatedBlankLayout />} />}
            >
              <Route
                path={ROUTES.MAP_BRANCHES_TO_COMPANY}
                element={<MapBranchesToCompany />}
              />
              <Route
                path={ROUTES.CHOOSE_COMPANY_TYPE}
                element={<ChooseCompanyType />}
              />
              <Route
                path={ROUTES.COMPANY_REGISTRATION_MODAL}
                element={<CompanyRegistrationModal />}
              />
              <Route
                path={ROUTES.COMPANY_REGISTER_WIZARD}
                element={<CompanyRegisterWizard />}
              />
              <Route
                path={ROUTES.SUBSCRIPTION_PAGE}
                element={<SubscriptionPage />}
              />
              <Route
                path={ROUTES.SMS_REGISTRATION}
                element={
                  <SubscriptionRoute
                    featureId="smschannel"
                    element={
                      <ProtectedRoute
                        resourceId="smschannel"
                        element={<SMSRegistration />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.WHATSAPP_REGISTRATION}
                element={
                  <SubscriptionRoute
                    featureId="whatsappchannel"
                    element={
                      <ProtectedRoute
                        resourceId="whatsappchannel"
                        element={<WhatsAppRegistration />}
                      />
                    }
                  />
                }
              />
            </Route>
            <Route path="/" element={<PrivateRoute element={<App />} />}>
              <Route
                path={ROUTES.HOME}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={<DashboardPage />}
                  />
                }
              />
              <Route path={ROUTES.API_HEALTH} element={<ApiHealthChecker />} />
              <Route
                path={ROUTES.MEMBER_ROUTINE_GENERATOR}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={
                      <ProtectedRoute
                        resourceId="members"
                        element={<MemberRoutineGenerator />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.MEMBER_REPORT}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={
                      <ProtectedRoute
                        resourceId="memberreport"
                        element={<HomePage />}
                      />
                    }
                  />
                }
              />
              {/* <Route
                path={`/routine/:routine_id/:value`}
                element={<RoutineExerciseViewPage />}
              /> */}
              <Route
                path={ROUTES.MEMBERS}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={
                      <ProtectedRoute
                        resourceId="members"
                        element={<MemberProfile />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ADD_MEMBER_PAGE}
                /*    element={
                <MemberAddPage />
              }  */ element={
                  <SubscriptionRoute
                    featureId="members"
                    element={
                      <ProtectedRoute
                        resourceId="members"
                        element={<MemberAddPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.EDIT_MEMBER_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={
                      <ProtectedRoute
                        resourceId="members"
                        element={<MemberEditPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ADD_QUICK_MEMBER_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={
                      <ProtectedRoute
                        resourceId="members"
                        element={<MemberQuickAddPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.PROFILE}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={
                      <ProtectedRoute
                        resourceId="personalprofile"
                        element={<ProfilePage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.BRANCHES}
                element={
                  <SubscriptionRoute
                    featureId="branches"
                    element={
                      <ProtectedRoute
                        resourceId="branches"
                        element={<BranchPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ADD_BRANCH_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="branches"
                    element={
                      <ProtectedRoute
                        resourceId="branches"
                        element={<AddBranchPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.UPDATE_BRANCH_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="branches"
                    element={
                      <ProtectedRoute
                        resourceId="branches"
                        element={<BranchUpdate />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.UPDATE_BRANCH_IMAGE_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="branches"
                    element={
                      <ProtectedRoute
                        resourceId="branches"
                        element={<BranchImageupdate />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.BRANCH_DETAILS}
                element={
                  <SubscriptionRoute
                    featureId="branches"
                    element={
                      <ProtectedRoute
                        resourceId="branches"
                        element={<BranchDetailsPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ROOMS}
                element={
                  <SubscriptionRoute
                    featureId="branches"
                    element={
                      <ProtectedRoute
                        resourceId="branches"
                        element={<RoomPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.MEMBER_DETAILS}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={
                      <ProtectedRoute
                        resourceId="members"
                        element={<MemberDetailsPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.MEMBER_HISTORY}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={
                      <ProtectedRoute
                        resourceId="members"
                        element={<MemberHistory />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.PACKAGE_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="packages"
                    element={
                      <ProtectedRoute
                        resourceId="packages"
                        element={<Package />}
                      />
                    }
                  />
                }
              />
              {/* <Route
                path={ROUTES.PACKAGE_IMPORT}
                element={
                  <SubscriptionRoute
                    featureId="packages"
                    element={
                      <ProtectedRoute
                        resourceId="packages"
                        element={<PackageImportPage />}
                      />
                    }
                  />
                }
              /> */}
              <Route
                path={ROUTES.PACKAGE_VIEW_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="packages"
                    element={
                      <ProtectedRoute
                        resourceId="packages"
                        element={<PackageViewPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.USERS}
                element={
                  <SubscriptionRoute
                    featureId="staff"
                    element={
                      <ProtectedRoute
                        resourceId="staff"
                        element={<UserPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CHALLENGE_CREATE}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={<CreateChallenge />}
                  />
                }
              />
              <Route
                path={ROUTES.CHALLENGES}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={<ChallengesTablePage />}
                  />
                }
              />
              <Route
                path={ROUTES.CHALLENGE_VIEW}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={<ChallengeViewPage />}
                  />
                }
              />
              <Route
                path={ROUTES.TRAINERS}
                element={
                  <SubscriptionRoute
                    featureId="staff"
                    element={
                      <ProtectedRoute
                        resourceId="staff"
                        element={<Trainers />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.TRAINER_DETAILS}
                element={
                  <SubscriptionRoute
                    featureId="staff"
                    element={
                      <ProtectedRoute
                        resourceId="staff"
                        element={<TrainerDetailsPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.USER_DETAILS}
                element={
                  <SubscriptionRoute
                    featureId="staff"
                    element={
                      <ProtectedRoute
                        resourceId="staff"
                        element={<AdminUserDetailPage />}
                      />
                    }
                  />
                }
              />
              {/* <Route path={`/companysettings`} element={<CompanySetting />} /> */}
              <Route
                path={ROUTES.COMPANY_PROFILE}
                element={
                  <ProtectedRoute
                    resourceId=""
                    element={<CompanyProfilePage />}
                  />
                }
              />
              <Route
                path={ROUTES.GRANT_LOAN}
                element={
                  <ProtectedRoute
                    resourceId="loans" // Assuming 'loans' resource ID for now, adjust if needed based on roleAccess
                    element={<GrantLoanPage />}
                  />
                }
              />
              <Route
                path={ROUTES.LEAD_INTEGRATION}
                element={<IntegrationsPage userId={""} />}
              />
              <Route
                path="/defaultroutine/:routineId"
                element={<Routinepage />}
              />
              {/* <Route
                path="/exercise/:routineId/:exerciseId"
                element={<ExerciseDetailPage />}
              /> */}
              <Route
                path={ROUTES.ROUTINE_PLAN}
                element={
                  <SubscriptionRoute
                    featureId="routine"
                    element={
                      <ProtectedRoute
                        resourceId="routine"
                        element={<RoutinePlanMain />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ROUTINE_PLAN_CREATE}
                element={
                  <SubscriptionRoute
                    featureId="routine"
                    element={
                      <ProtectedRoute
                        resourceId="routine"
                        element={<RoutinePlanCreate />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ROUTINE_PLAN_UPDATE}
                element={
                  <SubscriptionRoute
                    featureId="routine"
                    element={
                      <ProtectedRoute
                        resourceId="routine"
                        element={<RoutinePlanUpdate />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ROUTINE_DETAIL_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="routine"
                    element={
                      <ProtectedRoute
                        resourceId="routine"
                        element={<RoutinePlanSinglePage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CLASSES_SCHEDULE_CREATE}
                element={
                  <SubscriptionRoute
                    featureId="classes"
                    element={
                      <ProtectedRoute
                        resourceId="classes"
                        element={<CreateClasses isScheduleOnly />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CLASSES_SCHEDULE_EDIT}
                element={
                  <SubscriptionRoute
                    featureId="classes"
                    element={
                      <ProtectedRoute
                        resourceId="classes"
                        element={<CreateClasses isScheduleOnly />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CLASSES_DETAILS}
                element={
                  <SubscriptionRoute
                    featureId="classes"
                    element={
                      <ProtectedRoute
                        resourceId="classes"
                        element={<ClassDetailsPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CLASSES_CREATE}
                element={
                  <SubscriptionRoute
                    featureId="classes"
                    element={
                      <ProtectedRoute
                        resourceId="classes"
                        element={<CreateClasses />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CLASSES_IMPORT}
                element={
                  <SubscriptionRoute
                    featureId="classesimport"
                    element={
                      <ProtectedRoute
                        resourceId="classes"
                        element={<ClassesImportPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CLASS_BOOKING_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="classes"
                    element={
                      <ProtectedRoute
                        resourceId="classes"
                        element={<ClassBookingPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CLASSES_INSTANCES}
                element={
                  <SubscriptionRoute
                    featureId="classes"
                    element={
                      <ProtectedRoute
                        resourceId="classes"
                        element={<ClassInstancePage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CLASS_REPORT}
                element={
                  <SubscriptionRoute
                    featureId="classes"
                    element={
                      <ProtectedRoute
                        resourceId="classes"
                        element={<ClassReportPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CLASSES_INSTANCES_DETAILS}
                element={
                  <SubscriptionRoute
                    featureId="classes"
                    element={
                      <ProtectedRoute
                        resourceId="classes"
                        element={<ClassInstanceDetailsPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CLASSES_SCHEDULE_DETAILS}
                element={
                  <SubscriptionRoute
                    featureId="classes"
                    element={
                      <ProtectedRoute
                        resourceId="classes"
                        element={<ScheduleDetailsPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CLASSES}
                element={
                  <SubscriptionRoute
                    featureId="classes"
                    element={
                      <ProtectedRoute
                        resourceId="classes"
                        element={<ClassPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.EXPENSE}
                element={
                  <SubscriptionRoute
                    featureId="expenses"
                    element={
                      <ProtectedRoute
                        resourceId="expenses"
                        element={<ExpensePage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ADD_TRANSACTION}
                element={
                  <SubscriptionRoute
                    featureId="expenses"
                    element={
                      <ProtectedRoute
                        resourceId="expenses"
                        element={<AddTransactionPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.EXPENSE_VIEW_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="expenses"
                    element={
                      <ProtectedRoute
                        resourceId="expenses"
                        element={<ExpenseViewPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.RECURRING_EXPENSE_VIEW_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="expenses"
                    element={
                      <ProtectedRoute
                        resourceId="expenses"
                        element={<RecurringExpenseViewPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.PAYROLL_MAIN_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="employeepayrollreport"
                    element={
                      <ProtectedRoute
                        resourceId="employeepayrollreport"
                        element={<PayrollMainPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.PAYROLL_EMPLOYEES_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="employeepayrollreport"
                    element={
                      <ProtectedRoute
                        resourceId="employeepayrollreport"
                        element={<PayrollEmployeesPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.PAYROLL_MY_REPORT}
                element={
                  <SubscriptionRoute
                    featureId="employeepayrollreport"
                    element={
                      <ProtectedRoute
                        resourceId="employeepayrollreport"
                        element={<MyPayrollReport />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.APPLY_LEAVE}
                element={
                  <ProtectedRoute
                    resourceId="leave"
                    element={<LeaveSummaryPage />}
                  />
                }
              />
              <Route
                path={ROUTES.LEAVE_APPROVALS}
                element={
                  <ProtectedRoute
                    resourceId="leave" // Using same resourceId for now to ensure access
                    element={<LeaveApprovalPage />}
                  />
                }
              />
              <Route
                path={ROUTES.EXPENSE_EDIT_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="expenses"
                    element={
                      <ProtectedRoute
                        resourceId="expenses"
                        element={<ExpenseEditPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ADD_PAYMENT_RECEIVED}
                element={
                  <SubscriptionRoute
                    featureId="paymentreceived"
                    element={
                      <ProtectedRoute
                        resourceId="paymentreceived"
                        element={<AddPaymentReceivedPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ENQUIRY}
                element={
                  <SubscriptionRoute
                    featureId="enquiry"
                    element={
                      <ProtectedRoute
                        resourceId="enquiry"
                        element={<EnquiryPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ENQUIRY_VIEW}
                element={
                  <SubscriptionRoute
                    featureId="enquiry"
                    element={
                      <ProtectedRoute
                        resourceId="enquiry"
                        element={<EnquiryViewPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ENQUIRY_ADD}
                element={
                  <SubscriptionRoute
                    featureId="enquiry"
                    element={
                      <ProtectedRoute
                        resourceId="enquiry"
                        element={<EnquiryAddPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ENQUIRY_EDIT}
                element={
                  <SubscriptionRoute
                    featureId="enquiry"
                    element={
                      <ProtectedRoute
                        resourceId="enquiry"
                        element={<EnquiryEditPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ENQUIRY_MANAGEMENT}
                element={
                  <SubscriptionRoute
                    featureId="enquiry"
                    element={
                      <ProtectedRoute
                        resourceId="enquiry"
                        element={<EnquiryFormManagement />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ENQUIRY_SETTINGS}
                element={
                  <SubscriptionRoute
                    featureId="enquiry"
                    element={
                      <ProtectedRoute
                        resourceId="enquiry"
                        element={<EnquirySettings />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.BIOMETRIC_INFO}
                element={
                  <SubscriptionRoute
                    featureId="biometricdevice"
                    element={
                      <ProtectedRoute
                        resourceId="biometric"
                        element={<BiometricDevicePage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ADD_BIODEVICE}
                element={
                  <SubscriptionRoute
                    featureId="biometricdevice"
                    element={
                      <ProtectedRoute
                        resourceId="biometric"
                        element={<AddBiometricDevicePage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.EDIT_BIODEVICE}
                element={
                  <SubscriptionRoute
                    featureId="biometricdevice"
                    element={
                      <ProtectedRoute
                        resourceId="biometric"
                        element={<EditBiometricDevicePage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ATTENDANCE_REPORT}
                element={
                  <SubscriptionRoute
                    featureId="employeeattendancereport"
                    element={
                      <ProtectedRoute
                        resourceId="employeeattendancereport"
                        element={<EmployeeAttendance />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.EXPENSE_REPORT}
                element={
                  <SubscriptionRoute
                    featureId="expenses"
                    element={
                      <ProtectedRoute
                        resourceId="expenses"
                        element={<ExpenseReport />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.PAYROLL_REPORT}
                element={
                  <SubscriptionRoute
                    featureId="payroll"
                    element={
                      <ProtectedRoute
                        resourceId="employeepayrollreport"
                        element={<PayrollReportPage />}
                      />
                    }
                  />
                }
              />
              {/* <Route path="/testing" element={<TermsAndConditions />} /> */}
              <Route
                path={ROUTES.PRODUCT_SALES_REPORT}
                element={
                  <SubscriptionRoute
                    featureId="productreport"
                    element={
                      <ProtectedRoute
                        resourceId="productreport"
                        element={<ProductSalesReport />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.PACKAGE_SALES_REPORT}
                element={
                  <SubscriptionRoute
                    featureId="productreport"
                    element={
                      <ProtectedRoute
                        resourceId="productreport"
                        element={<PackageSalesReport />}
                      />
                    }
                  />
                }
              />
              {/* <Route path="/classes" element={<ClassesPage />} />
              <Route path="/classespage" element={<ClassesGymPage />} /> */}
              <Route
                path={ROUTES.EQUIPMENT}
                element={
                  <SubscriptionRoute
                    featureId="equipment"
                    element={
                      <ProtectedRoute
                        resourceId="equipment"
                        element={<EquipmentPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.EQUIPMENT_IMPORT}
                element={
                  <SubscriptionRoute
                    featureId="equipmentimport"
                    element={
                      <ProtectedRoute
                        resourceId="equipment"
                        element={<EquipmentImportPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ADD_EQUIPMENT}
                element={
                  <SubscriptionRoute
                    featureId="equipment"
                    element={
                      <ProtectedRoute
                        resourceId="equipment"
                        element={<AddEquipmentPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.UPDATE_EQUIPMENT}
                element={
                  <SubscriptionRoute
                    featureId="equipment"
                    element={
                      <ProtectedRoute
                        resourceId="equipment"
                        element={<EquipmentUpdate />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.SETTING_OVERVIEW}
                element={
                  <SubscriptionRoute
                    featureId="members"
                    element={<SettingOverviewPage />}
                  />
                }
              />
              <Route
                path={ROUTES.CHAT}
                // element={<ProtectedRoute resourceId="" element={<Chat />} />}
                element={
                  <SubscriptionRoute featureId="chat" element={<Chat />} />
                }
              />
              <Route
                path={ROUTES.ADD_NEW_STAFF}
                element={
                  <SubscriptionRoute
                    featureId="staff"
                    element={
                      <ProtectedRoute
                        resourceId="staff"
                        element={<AddNewUser />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.EDIT_STAFF_SALARY}
                element={
                  <SubscriptionRoute
                    featureId="staff"
                    element={
                      <ProtectedRoute
                        resourceId="staff"
                        element={<StaffSalaryPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.EDIT_STAFF_DOCUMENT}
                element={
                  <SubscriptionRoute
                    featureId="staff"
                    element={
                      <ProtectedRoute
                        resourceId="staff"
                        element={<CertificateUploadPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.COMPANY_SETTING}
                element={
                  <ProtectedRoute
                    resourceId="companyprofile"
                    element={
                      <CompanySettings /* actionPermission={getActionPermissionForRoute('/companysetting')} */
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.MANAGE_SUBSCRIPTION}
                element={
                  <ProtectedRoute
                    resourceId="subscription"
                    element={<Subscription />}
                  />
                }
              />
              <Route
                path={ROUTES.INVENTORY_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="inventory"
                    element={
                      <ProtectedRoute
                        resourceId="inventory"
                        element={<InventoryPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.INVENTORY_DETAILS_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="inventory"
                    element={
                      <ProtectedRoute
                        resourceId="inventory"
                        element={<InventoryViewPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.REWARDS_PAGE}
                element={
                  <ProtectedRoute
                    resourceId="members"
                    element={<RewardsPage />}
                  />
                }
              />
              <Route
                path={ROUTES.REFFERAL_SETTINGS}
                element={<ReferralSettings />}
              />
              <Route
                path={ROUTES.ADJUST_STOCK_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="inventory"
                    element={
                      <ProtectedRoute
                        resourceId="inventory"
                        element={<AdjustStockPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.PURCHASE_ORDER_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="purchaseorder"
                    element={
                      <ProtectedRoute
                        resourceId="purchaseorder"
                        element={<PurchaseOrderPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.CREATE_PURCHASE_ORDER}
                element={
                  <SubscriptionRoute
                    featureId="purchaseorder"
                    element={
                      <ProtectedRoute
                        resourceId="purchaseorder"
                        element={<CreatePurchaseOrderPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.EDIT_PURCHASE_ORDER}
                element={
                  <SubscriptionRoute
                    featureId="purchaseorder"
                    element={
                      <ProtectedRoute
                        resourceId="purchaseorder"
                        element={<CreatePurchaseOrderPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.PURCHASE_ORDER_DETAILS}
                element={
                  <SubscriptionRoute
                    featureId="purchaseorder"
                    element={
                      <ProtectedRoute
                        resourceId="purchaseorder"
                        element={<PurchaseOrderDetailsPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.BILLS_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="billing"
                    element={
                      <ProtectedRoute
                        resourceId="billing"
                        element={<BillsPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.BILL_DETAILS}
                element={
                  <SubscriptionRoute
                    featureId="billing"
                    element={
                      <ProtectedRoute
                        resourceId="billing"
                        element={<BillDetailsPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.BUSINESS_HOURS}
                element={
                  <ProtectedRoute
                    resourceId="businesshours"
                    element={
                      <BusinessHoursPage
                        actionPermission={getActionPermissionForRoute(
                          ROUTES.BUSINESS_HOURS,
                        )}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ATTENDANCE_SETTINGS}
                element={
                  <ProtectedRoute
                    resourceId="staffattendancesettings"
                    element={<AttendanceSettingsView />}
                  />
                }
              />
              <Route
                path={ROUTES.MEMBER_ATTENDANCE_SETTINGS}
                element={
                  <ProtectedRoute
                    resourceId="memberattendancesettings"
                    element={<MemberAttendanceSetting />}
                  />
                }
              />
              <Route
                path={ROUTES.PAYROLL}
                element={
                  <SubscriptionRoute
                    featureId="payroll"
                    element={
                      <ProtectedRoute
                        resourceId="payrollsettings"
                        element={<PayrollPage />}
                      />
                    }
                  />
                }
              />
              {/* Loan Management Routes */}
              <Route
                path={ROUTES.LOAN_TYPES}
                element={
                  <ProtectedRoute
                    resourceId="loans"
                    element={<LoanTypesPage />}
                  />
                }
              />
              <Route
                path={ROUTES.LOAN_LIST}
                element={
                  <ProtectedRoute resourceId="loans" element={<LoanList />} />
                }
              />
              <Route
                path={ROUTES.LOAN_DETAILS}
                element={
                  <ProtectedRoute
                    resourceId="loans"
                    element={<LoanDetails />}
                  />
                }
              />
              <Route
                path={ROUTES.HOLIDAY}
                element={
                  <ProtectedRoute
                    resourceId="holiday"
                    element={<HolidayPage />}
                  />
                }
              />
              <Route
                path={ROUTES.BANKING}
                element={
                  <ProtectedRoute resourceId="banking" element={<Banking />} />
                }
              />
              <Route
                path={ROUTES.PAYMENT}
                element={
                  <ProtectedRoute
                    resourceId="paymentcollection"
                    element={<PaymentPage />}
                  />
                }
              />
              <Route
                path={ROUTES.EDIT_PAYMENT_METHOD}
                element={
                  <ProtectedRoute
                    resourceId="paymentcollection"
                    element={<UPIEditPage />}
                  />
                }
              />
              <Route
                path={ROUTES.ADD_PAYMENT_METHOD}
                element={
                  <ProtectedRoute
                    resourceId="paymentcollection"
                    element={<AddPaymentMethodPage />}
                  />
                }
              />
              <Route
                path={ROUTES.PACKAGE_CREATE}
                element={
                  <SubscriptionRoute
                    featureId="packages"
                    element={
                      <ProtectedRoute
                        resourceId="packages"
                        element={<PackageCreatePage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ADD_BANKING_PAGE}
                element={
                  <ProtectedRoute
                    resourceId="banking"
                    element={<AddBankingPage />}
                  />
                }
              />
              <Route
                path={ROUTES.PREFIX_PAGE}
                element={
                  <ProtectedRoute
                    resourceId="customprefix"
                    element={<PrefixPage />}
                  />
                }
              />
              <Route
                path={ROUTES.EMAIL_TEMPLATE_MANAGE}
                element={
                  <ProtectedRoute
                    resourceId=""
                    element={<EmailTemplateManage />}
                  />
                }
              />
              <Route
                path={ROUTES.CREATE_EMAIL_TEMPLATE}
                element={
                  <ProtectedRoute
                    resourceId=""
                    element={<EmailTemplateCreatePage />}
                  />
                }
              />
              <Route
                path={ROUTES.PAYMENT_RECEIVED_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="paymentreceived"
                    element={
                      <ProtectedRoute
                        resourceId="paymentreceived"
                        element={<PaymentReceivedPage />}
                      />
                    }
                  />
                }
              />
              {/* <Route
                path={ROUTES.RECURRING_PAYMENTS}
                element={
                  <SubscriptionRoute
                    featureId="paymentreceived"
                    element={
                      <ProtectedRoute
                        resourceId="paymentreceived"
                        element={<RecurringPaymentPage />}
                      />
                    }
                  />
                }
              /> */}
              <Route
                path={ROUTES.PAYMENT_RECEIVED_VIEW}
                element={
                  <SubscriptionRoute
                    featureId="paymentreceived"
                    element={
                      <ProtectedRoute
                        resourceId="paymentreceived"
                        element={<PaymentReceivedViewPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.PAYMENT_RECEIVED_EDIT}
                element={
                  <SubscriptionRoute
                    featureId="paymentreceived"
                    element={
                      <ProtectedRoute
                        resourceId="paymentreceived"
                        element={<PaymentReceivedEditPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.INVOICE_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="invoice"
                    element={
                      <ProtectedRoute
                        resourceId="invoice"
                        element={<InvoicePage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.INVOICE_DETAILS_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="invoice"
                    element={
                      <ProtectedRoute
                        resourceId="invoice"
                        element={<InvoiceDetailPage />}
                      />
                    }
                  />
                }
              />
              <Route path="/memberfreeze" element={<MemberFreeze />} />
              <Route
                path="/recurring-expenses"
                element={
                  <SubscriptionRoute
                    featureId="expenses"
                    element={
                      <ProtectedRoute
                        resourceId="expenses"
                        element={<RecurringExpenseTable />}
                      />
                    }
                  />
                }
              />
              <Route
                path="/recurring-expenses/add"
                element={
                  <SubscriptionRoute
                    featureId="expenses"
                    element={
                      <ProtectedRoute
                        resourceId="expenses"
                        element={<RecuringExpenses />}
                      />
                    }
                  />
                }
              />
              <Route
                path="/recurring-expenses/edit/:transactionId"
                element={
                  <SubscriptionRoute
                    featureId="expenses"
                    element={
                      <ProtectedRoute
                        resourceId="expenses"
                        element={<RecuringExpenses />}
                      />
                    }
                  />
                }
              />
              <Route
                path="/external-members"
                element={<ExternalMembersTable />}
              />
              <Route
                path={ROUTES.ADD_INVOICE_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="invoice"
                    element={
                      <ProtectedRoute
                        resourceId="invoice"
                        element={<AddInvoicePage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.MEMBER_IMPORT_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="memberimport"
                    element={
                      <ProtectedRoute
                        resourceId="memberimport"
                        element={<MemberImportPage />}
                      />
                    }
                  />
                }
              />
              {/* Ui Component  */}
              <Route path="/branchui" element={<BranchDetailspage />} />
              <Route path="/template" element={<RoutineTemplate />} />
              {/* <Route
                path="/exercise/:routineId/:exerciseId"
                element={<ExerciseDetailPage />}
              /> */}
              <Route
                path={ROUTES.STAFF_IMPORT_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="staffimport"
                    element={
                      <ProtectedRoute
                        resourceId="staffimport"
                        element={<StaffImportPage />}
                      />
                    }
                  />
                }
              />
              {/* <Route path="/demo" element={<DemoRoutineGenerator />} /> */}
              {/* <Route path='/communication' element={<CommunicationConfigPage />} />
              <Route path="/gstslab" element={<GSTSlabConfigPage />} />
              <Route path="/graceperiod" element={<GracePeriodConfigPage />} /> */}
              <Route
                path={ROUTES.WHATSAPP_CONFIG}
                element={
                  <SubscriptionRoute
                    featureId="whatsappchannel"
                    element={
                      <ProtectedRoute
                        resourceId="whatsappchannel"
                        element={<WhatsAppConfigPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.WHATSAPP_MAPPING}
                element={
                  <SubscriptionRoute
                    featureId="whatsappchannel"
                    element={
                      <ProtectedRoute
                        resourceId="whatsappchannel"
                        element={<WhatsAppTemplateMapping />}
                      />
                    }
                  />
                }
              />
              <Route path={ROUTES.WHATSAPP} element={<WhatsApp />} />
              // SMS Routes
              <Route
                path={ROUTES.SMS_CONFIG}
                element={
                  <SubscriptionRoute
                    featureId="smschannel"
                    element={
                      <ProtectedRoute
                        resourceId="smschannel"
                        element={<SMSConfigPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.SMS_MAPPING}
                element={
                  <SubscriptionRoute
                    featureId="smschannel"
                    element={
                      <ProtectedRoute
                        resourceId="smschannel"
                        element={<SMSTemplateMapping />}
                      />
                    }
                  />
                }
              />
              <Route path={ROUTES.SMS} element={<SMS />} />
              // Email Route
              <Route
                path={ROUTES.EMAIL_SMTP_CONFIG}
                element={
                  <SubscriptionRoute
                    featureId="emailchannel"
                    element={
                      <ProtectedRoute
                        resourceId="emailchannel"
                        element={<EmailSMTPConfig />}
                      />
                    }
                  />
                }
              />
              <Route path={ROUTES.EMAIL} element={<Email />} />
              <Route
                path={ROUTES.ALL_PRODUCTS_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="products"
                    element={
                      <ProtectedRoute
                        resourceId="products"
                        element={<AllProductsPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.ADD_PRODUCT_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="products"
                    element={
                      <ProtectedRoute
                        resourceId="products"
                        element={<AddProductPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.EDIT_PRODUCT_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="products"
                    element={
                      <ProtectedRoute
                        resourceId="products"
                        element={<EditProductPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.PRODUCT_DETAILS_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="products"
                    element={
                      <ProtectedRoute
                        resourceId="products"
                        element={<ProductDetailsPage />}
                      />
                    }
                  />
                }
              />
              <Route
                path={ROUTES.PRODUCT_IMPORT_PAGE}
                element={
                  <SubscriptionRoute
                    featureId="productimport"
                    element={
                      <ProtectedRoute
                        resourceId="productimport"
                        element={<ProductImportPage />}
                      />
                    }
                  />
                }
              />
              {/* <Route
                path={ROUTES.NEW_SUBSCRIPTION_PAGE}
                element={<MemberSubscriptionPage />}
              /> */}
              <Route
                path={ROUTES.STAFF_LOCATION_ACCESS_CONTROL_PAGE}
                element={<StaffLocationAccessControl />}
              />
              <Route
                path={ROUTES.ADD_GEO_RESTRICTION}
                element={<GeoRestrictionCreate />}
              />
              <Route
                path={ROUTES.ADD_IP_RESTRICTION}
                element={<IPRestrictionCreate />}
              />
              <Route
                path={ROUTES.EDIT_GEO_RESTRICTION}
                element={<GeoRestrictionEdit />}
              />
              <Route
                path={ROUTES.EDIT_IP_RESTRICTION}
                element={<IPRestrictionEdit />}
              />
              <Route path={ROUTES.FORM_BUILDER_PAGE} element={<FormsList />} />
              <Route path={ROUTES.CREATE_FORM_PAGE} element={<FormBuilder />} />
              <Route path={ROUTES.EDIT_FORM_PAGE} element={<FormBuilder />} />
              <Route
                path={ROUTES.LANDING_PAGES_LIST}
                element={<LandingPagesList />}
              />
              <Route
                path={ROUTES.LANDING_PAGE_BUILDER}
                element={<LandingPageBuilder />}
              />
              {/* <Route
                path="/gym/dietplandetail"
                element={<DietPlanDetailsPage />}
              />
              <Route path="/gym/classes" element={<ClassesGymPage />} />
              <Route
                path="/gym/classes/tutor"
                element={<TutorManagementGymPage />}
              />
              <Route
                path="/gym/classes/create"
                element={<ClassesCreateGymPage />}
              />
              <Route path="/spa/booking" element={<SPABookingPage />} />
              <Route path="/spa/config" element={<SpaConfigPage />} />
              <Route path="/spa/memberships" element={<Memberships />} />
              <Route
                path="/spa/membershipcreate"
                element={<MembershipsCreatePage />}
              />
              <Route path="/spa/staff" element={<AddStaffPage />} />
              <Route path="/spa/addbooking" element={<BookAppointmentPage />} />
              <Route path="/spa/pos" element={<SpaBillingPage />} />
              <Route path="/spa/addservice" element={<AddServicePage />} />
              <Route path="/turf/booking" element={<TurfBookingPage />} />
              <Route path="/turf/setup" element={<TurfSetupPage />} /> */}
            </Route>
            {/* <Route path="/spa/addproduct" element={<AddProductPage />} /> */}

            <Route
              path="/"
              element={<PrivateRoute element={<AuthenticatedBlankLayout />} />}
            >
              <Route
                path={ROUTES.NEW_SUBSCRIPTION_PAGE}
                element={<MemberSubscriptionPage />}
              />
            </Route>
            <Route
              path={ROUTES.COMPANY_REGISTRATION}
              element={<CompanyRegistration />}
            />
            <Route
              path={ROUTES.COMPANY_CLAIM}
              element={<CompanyRegistrationSearch />}
            />
            <Route path={ROUTES.COMPANY_CLAIMED} element={<CompanyClaimed />} />
            {/* <Route
              path={ROUTES.COMPANY_CREATE_NEW}
              element={<CompanyRegistrationNotFound />}
            /> */}
            <Route path={ROUTES.PAYMENT_PAGE} element={<BenchPaymentPage />} />
            <Route
              path={ROUTES.PAYMENT_SUCCESS_PAGE}
              element={<SubscriptionSuccess />}
            />
            <Route
              path={ROUTES.MEMBER_PAYMENT_SUCCESS_PAGE}
              element={<MemberSubscriptionSuccess />}
            />
            <Route
              path="/appointment/public/booking"
              element={<AppointmentBookingPage />}
            />
            <Route
              path="/appointment/public/booking/:branchId"
              element={<AppointmentBookingPage />}
            />

            <Route path="/uploadworkout" element={<UploadRoutine />} />

            <Route path={"ui"} element={<UIViewPage />} />
            <Route
              path={ROUTES.PAYMENT_FAILED_PAGE}
              element={<PaymentFailed />}
            />
            {/* Fallback for non-existing routes */}
            <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
            <Route path={ROUTES.USER_GUIDE} element={<UserGuide />} />
            <Route
              path="/test-notifications"
              element={<NotificationTestPage />}
            />
          </Routes>
        </BrowserRouter>

        <ToastContainer />
      </Suspense>
    </AnimatePresence>
  );
}

export default AuthenticatedApp;
